import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Button,
  Box,
} from "@mui/material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import logo from "../../views/schedules/components/PDF/img/image-Photoroom (1).png";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../utils/axiosConfig";
import { authService } from "../../utils/authService";

const QuotationForm = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    quotationNumber: new Date().getFullYear() + "-",
    recipient: "URBANIZACION CASTELLI PH",
    recipientName: "Sra. Karen",
    recipientRole: "Administrador// Representante legal",
    serviceTitle: "REPARACIÓN TALANQUERA DE ENTRADA",
    technicalReport:
      "Atendiendo novedad por colisión de camión en la talanquera de entrada, se identifica que la grapa de la talanquera tuvo afectaciones considerables por lo que se requiere realizar el cambio; teniendo en cuenta que esta grapa es de un modelo antiguo que se encuentra agotado, optamos por el desmonte para realizar fabricación en fundición; a continuación, relacionamos presupuesto económico.",
    descriptionImage: "https://via.placeholder.com/600x300",
    paymentAmount: "$ 114.000 - $ 714.000",
    warranty:
      "3 meses sobre los trabajos realizados a partir de la entrega del trabajo. La garantía no cubre daños ocasionados por eventos externos como descargas atmosféricas, inundación, vandalismo, colisión, entre otros similares.",
    fabricationTime: "15 días hábiles",
    executionTime: "1 día hábil",
    closingRemarks:
      "Estaremos atentos para resolver sus dudas o inquietudes. Será un gusto atenderles, ya que como ustedes tenemos un compromiso con las copropiedades de seriedad y transparencia.",
    contactName: "Janeth Acosta Colorado",
    contactRole: "Ejecutiva Comercial y de Servicios",
    offerValidity: "", // Nuevo campo para la vigencia de la oferta
  });

  // Estado para almacenar la URL de la firma del usuario
  const [userSignature, setUserSignature] = useState("");
  const userId = authService.getUserData().id;

  // Al montar el componente, traemos la firma del endpoint de usuarios
  useEffect(() => {
    axiosInstance
      .get("/users/" + userId)
      .then((response) => {
        // Se asume que la URL de la firma se encuentra en response.data.signature_url
        setUserSignature(response.data.signature_url);
      })
      .catch((error) => {
        console.error("Error fetching user signature:", error);
      });
  }, [userId]);

  const quotationRef = useRef(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, descriptionImage: reader.result });
    };
    if (file) {
      reader.readAsDataURL(file);
    }
  };

  const generatePDF = () => {
    const input = quotationRef.current;
    const options = {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: true,
      imageTimeout: 0,
    };

    html2canvas(input, options).then((canvas) => {
      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const usableWidth = pageWidth - 2 * margin;
      const usableHeight = pageHeight - 2 * margin;
      const scale = usableWidth / canvas.width;
      const scaledCanvasHeight = canvas.height * scale;

      if (scaledCanvasHeight <= usableHeight) {
        pdf.addImage(
          imgData,
          "PNG",
          margin,
          margin,
          usableWidth,
          scaledCanvasHeight
        );
      } else {
        const totalPages = Math.ceil(scaledCanvasHeight / usableHeight);
        for (let i = 0; i < totalPages; i++) {
          const sourceY = i * (usableHeight / scale);
          const pageCanvas = document.createElement("canvas");
          pageCanvas.width = canvas.width;
          pageCanvas.height = Math.min(
            canvas.height - sourceY,
            usableHeight / scale
          );
          const ctx = pageCanvas.getContext("2d");
          ctx.drawImage(
            canvas,
            0,
            sourceY,
            canvas.width,
            pageCanvas.height,
            0,
            0,
            canvas.width,
            pageCanvas.height
          );
          const pageImgData = pageCanvas.toDataURL("image/png", 1.0);
          if (i > 0) pdf.addPage();
          pdf.addImage(
            pageImgData,
            "PNG",
            margin,
            margin,
            usableWidth,
            pageCanvas.height * scale
          );
        }
      }

      pdf.save("quotation.pdf");
    });
  };

  return (
    <div
      style={{
        padding: "20px",
        maxWidth: "800px",
        margin: "auto",
        fontSize: "0.5em",
      }}
    >
      <Card sx={{ maxWidth: 800, margin: "auto", mt: 4, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h5" component="div" gutterBottom>
            {t("quotationDetails")}
          </Typography>

          <Box component="form" noValidate autoComplete="off">
            <Grid container spacing={2}>
              {/* Fila para Quotation Number y Vigencia de la Oferta */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("quotationNumber")}
                  name="quotationNumber"
                  value={formData.quotationNumber}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("offerValidity")}
                  name="offerValidity"
                  value={formData.offerValidity}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              {/* Sección: Dirigido a */}
              <Grid item xs={12}>
                <Typography variant="h6">{t("directedTo")}:</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t("recipient")}
                  name="recipient"
                  value={formData.recipient}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t("recipientName")}
                  name="recipientName"
                  value={formData.recipientName}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t("recipientRole")}
                  name="recipientRole"
                  value={formData.recipientRole}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              {/* Sección: Service Title */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("serviceTitle")}
                  name="serviceTitle"
                  value={formData.serviceTitle}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              {/* Sección: Technical Report */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={t("technicalReport")}
                  name="technicalReport"
                  value={formData.technicalReport}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              {/* Sección: Subir Imagen de Descripción */}
              <Grid item xs={12}>
                <Typography variant="subtitle1">
                  {t("uploadDescriptionImage")}:
                </Typography>
                <Button variant="contained" component="label" sx={{ mt: 1 }}>
                  {t("uploadImage")}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleImageChange}
                  />
                </Button>
              </Grid>

              {/* Sección: Forma de pago, Garantía y Tiempo de Fabricación */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t("paymentMethod")}
                  name="paymentAmount"
                  value={formData.paymentAmount}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t("warranty")}
                  name="warranty"
                  value={formData.warranty}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t("fabricationTime")}
                  name="fabricationTime"
                  value={formData.fabricationTime}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              {/* Sección: Tiempo de ejecución */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("executionTime")}
                  name="executionTime"
                  value={formData.executionTime}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              {/* Sección: Closing Remarks */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={t("closingRemarks")}
                  name="closingRemarks"
                  value={formData.closingRemarks}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              {/* Sección: Contacto */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("contactName")}
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("contactRole")}
                  name="contactRole"
                  value={formData.contactRole}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Contenido que se renderiza en el PDF */}
      <div
        ref={quotationRef}
        style={{
          background: "#fff",
          padding: "15px",
          margin: "20px auto",
          maxWidth: "600px",
          fontFamily: "Arial, sans-serif",
          lineHeight: "1.4",
          fontSize: "12px",
        }}
      >
        <img
          src={logo}
          alt="Logo"
          style={{ display: "block", margin: "0 auto 10px", width: "120px" }}
        />
        <h2 style={{ textAlign: "center", fontSize: "14px" }}>
          O.M.C Ingeniería S.A.S
        </h2>
        <p style={{ textAlign: "center", fontSize: "12px" }}>
          {new Date().toLocaleDateString("es-CO")}
        </p>
        <p style={{ textAlign: "center", fontWeight: "bold" }}>
          {t("quotation")}: {formData.quotationNumber}
        </p>
        {/* Mostrar la vigencia de la oferta en el PDF */}
        {formData.offerValidity && (
          <p style={{ textAlign: "center", fontWeight: "bold" }}>
            {t("offerValidity")}: {formData.offerValidity}
          </p>
        )}
        <h3 style={{ fontSize: "12px" }}>{t("directedTo")}:</h3>
        <p>
          <strong>{formData.recipient}</strong>
        </p>
        <p>{formData.recipientName}</p>
        <p>{formData.recipientRole}</p>
        <h3 style={{ fontSize: "12px" }}>{formData.serviceTitle}</h3>
        <p style={{ textAlign: "justify", fontSize: "12px" }}>
          En O.M.C Ingeniería, estamos comprometidos con el desempeño de los
          equipos. Ponemos a su servicio nuestra experiencia en diferentes
          sectores de transporte, control de tráfico y movilidad, siempre con el
          objetivo de que sus equipos operen en forma correcta, segura y
          conserven su vida útil, así como la transparencia en nuestros
          procedimientos y procesos, características de nuestro equipo de
          trabajo y el enfoque a nuestros clientes.
        </p>
        <h4 style={{ fontSize: "12px" }}>Informe Técnico</h4>
        <p style={{ textAlign: "justify", fontSize: "12px" }}>
          {formData.technicalReport}
        </p>
        <img
          src={formData.descriptionImage}
          alt="Descripción"
          style={{
            width: "90%",
            margin: "30px auto",
            display: "block",
            borderRadius: "8px",
          }}
        />
        <h4 style={{ fontSize: "12px" }}>Forma de pago:</h4>
        <p>{formData.paymentAmount}</p>
        <h4 style={{ fontSize: "12px" }}>Garantía:</h4>
        <p>{formData.warranty}</p>
        <h4 style={{ fontSize: "12px" }}>Tiempo de Fabricación:</h4>
        <p>{formData.fabricationTime}</p>
        <h4 style={{ fontSize: "12px" }}>Tiempo de ejecución:</h4>
        <p>{formData.executionTime}</p>
        <p>{formData.closingRemarks}</p>
        <h4 style={{ fontSize: "12px", marginTop: "20px" }}>Cordialmente:</h4>
        <p>{formData.contactName}</p>
        <p>{formData.contactRole}</p>
        <div style={{ marginTop: "20px" }}>
          <img
            src={userSignature}
            alt={t("userSignature")}
            style={{ width: "150px" }}
          />
        </div>
      </div>

      <button
        onClick={generatePDF}
        style={{
          marginTop: "20px",
          background: "#28a745",
          color: "white",
          padding: "10px",
          borderRadius: "8px",
          border: "none",
          width: "100%",
          cursor: "pointer",
          fontSize: "14px",
        }}
      >
        {t("generatePDF")}
      </button>
    </div>
  );
};

export default QuotationForm;
